<template>
  <router-view />
</template>

<script setup>
import { onMounted, onBeforeUnmount } from 'vue';
import { socketService } from "@/services/socketService";
import { useDeviceVerifierStore } from '@/stores/deviceVerifier';
import { useSystemSettingsStore } from '@/stores/systemSettings';
import { useBroadcastComposable } from "@/composables/useBroadcastComposable";
import { useLanguage } from "@/composables/useLanguage";
import { usePos } from '@/composables/usePos';
import { usePosEventListener } from '@/composables/usePosEventListener';
import { useSystemSettings } from "@/composables/useSystemSettings";

const { listen, removeListener } = socketService;

const { getIsVerified } = useDeviceVerifierStore();
const { setupPosSocketConnection } = useBroadcastComposable();
const { getDefaultLanguage } = useLanguage();
const { fetchPosClientId } = usePos();
const { getPosReloadEventName, handlePosReload, getPosSystemSettingsEventName, updateSystemLogo, updateSystemName, handleSystemSettingsEvent } = usePosEventListener();
const { getLanguage, setLanguage } = useSystemSettingsStore();
const { fetchSystemSettings } = useSystemSettings();

const posReloadEvent = getPosReloadEventName();
const posSystemSettingsEvent = getPosSystemSettingsEventName();

onMounted(async () => {
  if (!getLanguage()) {
    setLanguage(getDefaultLanguage());
  }

  /**
   * Check if the device is verified.
   * If it is verified, setup the POS socket connection and listen for events.
   */
  if (getIsVerified()) {
    /**
     * Setup pos socket connection.
     * Also listen for "kiosk_reload" event.
     */
    await setupPosSocketConnection();
    listen(posReloadEvent, async (receivedData) => {
      /* Ensure pos uuid is set in localStorage. */
      await fetchPosClientId();
      /* Handle "pos_reload" event */
      await handlePosReload(receivedData);
    });

    listen(posSystemSettingsEvent, async (receivedData) => {
      /* Handle "systemSettings" event */
      await handleSystemSettingsEvent(receivedData)
    });

    if (localStorage.getItem('fetchCustomSystemSettings')) {
      localStorage.removeItem('fetchCustomSystemSettings');
      const systemSettings = await fetchSystemSettings();
      if (systemSettings) {
        /**
         * Update the system settings in the store.
         */
        updateSystemLogo(systemSettings?.logo);
        updateSystemName(systemSettings?.name);
      }
    }
  }
});

onBeforeUnmount(() => {
  removeListener(posReloadEvent);
  removeListener(posSystemSettingsEvent);
});
</script>

<style lang="css">
@import 'styles/slick.css';
@import 'styles/slick-theme.css';
@import 'styles/style.css';
</style>